// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import NewsletterTemplate from "./newsletter-template.js"
import TableSearch from "./table-search.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "newsletter_template_id": {
      "column": {
        "default": null,
        "name": "newsletter_template_id",
        "null": false,
        "type": null
      },
      "name": "newsletter_template_id",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "table_search_id": {
      "column": {
        "default": null,
        "name": "table_search_id",
        "null": false,
        "type": null
      },
      "name": "table_search_id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "NewsletterTemplateTableSearch",
  "collectionKey": "newsletter_template_table_searches",
  "collectionName": "newsletter_template_table_searches",
  "i18nKey": "newsletter_template_table_search",
  "camelizedLower": "newsletterTemplateTableSearch",
  "name": "NewsletterTemplateTableSearch",
  "nameDasherized": "newsletter-template-table-search",
  "pluralName": "newsletter_template_table_searches",
  "ransackable_associations": [
    {
      "className": "NewsletterTemplateTableSearch::Audit",
      "collectionName": "newsletter_template_table_search_audits",
      "foreignKey": "newsletter_template_table_search_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "NewsletterTemplateTableSearchAudit",
      "through": null
    },
    {
      "className": "NewsletterTemplateTableSearch::Audit",
      "collectionName": "newsletter_template_table_search_audits",
      "foreignKey": "newsletter_template_table_search_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "NewsletterTemplateTableSearchAudit",
      "through": null
    },
    {
      "className": "NewsletterTemplate",
      "collectionName": "newsletter_templates",
      "foreignKey": "newsletter_template_id",
      "name": "newsletter_template",
      "macro": "belongs_to",
      "resource_name": "NewsletterTemplate",
      "through": null
    },
    {
      "className": "ApiMakerTable::TableSearch",
      "collectionName": "table_searches",
      "foreignKey": "table_search_id",
      "name": "table_search",
      "macro": "belongs_to",
      "resource_name": "TableSearch",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "newsletter_template_id",
      "column": {
        "default": null,
        "name": "newsletter_template_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "table_search_id",
      "column": {
        "default": null,
        "name": "table_search_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "NewsletterTemplate",
      "collectionName": "newsletter_templates",
      "foreignKey": "newsletter_template_id",
      "name": "newsletter_template",
      "macro": "belongs_to",
      "resource_name": "NewsletterTemplate",
      "through": null
    },
    {
      "className": "ApiMakerTable::TableSearch",
      "collectionName": "table_searches",
      "foreignKey": "table_search_id",
      "name": "table_search",
      "macro": "belongs_to",
      "resource_name": "TableSearch",
      "through": null
    }
  ],
  "paramKey": "newsletter_template_table_search",
  "primaryKey": "id"
}

/** Frontend model for NewsletterTemplateTableSearch. */
export default class NewsletterTemplateTableSearch extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  newsletterTemplateId() {
    return this.readAttributeUnderscore("newsletter_template_id")
  }

  /** @returns {boolean} */
  hasNewsletterTemplateId() {
    const value = this.newsletterTemplateId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  tableSearchId() {
    return this.readAttributeUnderscore("table_search_id")
  }

  /** @returns {boolean} */
  hasTableSearchId() {
    const value = this.tableSearchId()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./newsletter-template.js").default | null} */
  newsletterTemplate() {
    return this._readBelongsToReflection({modelClass: NewsletterTemplate, reflectionName: "newsletter_template"})
  }

  /** @returns {Promise<import("./newsletter-template.js").default | null>} */
  loadNewsletterTemplate() {
    if (!("newsletterTemplateId" in this)) throw new Error("Foreign key method wasn't defined: newsletterTemplateId")

    const id = this.newsletterTemplateId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "newsletter_template",
        model: this,
        modelClass: NewsletterTemplate
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./newsletter-template.js").default | null>} */
  ensureNewsletterTemplateLoaded() {
    return this.ensureAssociationLoaded("newsletter_template")
  }

  /** @returns {import("./table-search.js").default | null} */
  tableSearch() {
    return this._readBelongsToReflection({modelClass: TableSearch, reflectionName: "table_search"})
  }

  /** @returns {Promise<import("./table-search.js").default | null>} */
  loadTableSearch() {
    if (!("tableSearchId" in this)) throw new Error("Foreign key method wasn't defined: tableSearchId")

    const id = this.tableSearchId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "table_search",
        model: this,
        modelClass: TableSearch
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./table-search.js").default | null>} */
  ensureTableSearchLoaded() {
    return this.ensureAssociationLoaded("table_search")
  }
}
